import { chakra, Icon, useSlotRecipe } from '@chakra-ui/react'
import type { ElementType } from 'react'
import type { ConditionalValue } from '@chakra-ui/react'

type DialogAccentProps = {
  /** Icon rendered centred in the accent stripe. */
  icon: ElementType
  /** State color of the stripe. Defaults to the informational palette. */
  colorPalette?: ConditionalValue<string>
}

/**
 * The accent stripe on the leading edge of a dialog.
 *
 * Render it as the first child of `Dialog.Content` and set `accented` on
 * `Dialog.Root`:
 *
 * ```tsx
 * <Dialog.Root accented>
 *   …
 *   <Dialog.Content>
 *     <DialogAccent icon={LuTriangleAlert} colorPalette="red" />
 * ```
 *
 * The stripe width, placement, and color roles live in the dialog slot recipe;
 * call sites only choose the icon and the state palette.
 */
export function DialogAccent({ icon, colorPalette = 'blue' }: DialogAccentProps) {
  const recipe = useSlotRecipe({ key: 'dialog' })
  const styles = recipe({ accented: true })

  return (
    <chakra.div css={styles.accent} colorPalette={colorPalette} aria-hidden="true">
      <Icon as={icon} boxSize="8" />
    </chakra.div>
  )
}
